// DO NOT EDIT -- generated from fs/api/metrics/metrics.proto

import * as protobuf from '../../../google/protobuf';
import * as search from '../search';
import * as segment from '../segment';
import * as vars from '../vars';

export interface Computation {
  /** Metric for computation. */
  metric: Metric | null;
  /**
   * The time window for which solr data will be queried for the metric
   * This is a required field to compute a metric.
   */
  timeRange: segment.TimeRange | null;
  /**
   * The time range over which metric results will be determined.
   * 
   * If this is not a rolling window request, this can be omitted, and time_range will be used.
   * 
   * If this is a rolling window request, then time_range, segment.time_range etc, must go back to the start
   * of the first window.  This parameter is then supplied to tell the server what time range we actually want
   * results over.
   * 
   * For example, if the first day of the time range is Nov 1, and the rolling window is a 7 day window, then
   * the first window is [Oct 26, Nov 2).  time_range should then start on or before Oct 26, and
   * time_interval_range should start on Nov 1.
   */
  timeIntervalRange: segment.TimeRange | null;
  /**
   * Time interval determines the time buckets for the computation.
   * This is a required field to compute a metric.
   */
  timeInterval: segment.TimeInterval;
  /**
   * SegmentDefs to filter events for the supplied groups of individuals.
   * This is an optional field, not providing any segment definitions
   * means selecting all events in the time range.
   * Providing multiple segment definitions performs an intersection search (i.e. only individuals that are
   * part of each segment will be considered).
   */
  segmentDefinitions: segment.SegmentDefinition[];
  /**
   * Matching behaviors for which the results should be scoped.
   * The most common use of matching behaviors is to supply a matching behaviors stanza per segment definition, taking
   * the behaviors and constraints verbatim, with an additional rescoping.
   * The resulting events will match the intersection of all matching behaviors.
   */
  matchingBehaviors: search.EventSearch.MatchingBehaviors[];
  /**
   * Dimension settings for the card.
   * This is an optional field to support grouping.
   */
  dimensionSettings: DimensionSettings | null;
}

export interface DimensionSettings {
  /**
   * Legacy field, use dimensions instead
   * 
   * @deprecated
   */
  dimension: Dimension | null;
  /** The dimension(s) to group results */
  dimensions: Dimension[];
  /** Sort order for the results. */
  sortOrder: SortOrder;
  /**
   * Deprecated: Use sort_type instead
   * Force sorting to be applied based on default event count rather than selected aggregation (if different).
   * Note: This is currently not implemented for all metrics. Double check support before using.
   * 
   * @deprecated
   */
  sortByDefault: boolean;
  /** Sort type for the results. Only supported for compare to past. Also only SORT_ORDER_DESC is supported. Any ASC will be ignored */
  sortType: SortType;
  /**
   * Limit on total groups in the result.
   * Ignored if underlying per dimension limits are set.
   */
  limit: number;
  /**
   * Whether or not to compute the metric at the top level (in addition to the dimensionalized results).
   * The top level result will have no group ids. If requested for a dual metric, it will be based on the first event results.
   */
  computeTopLevel: boolean;
  /**
   * For multiple dimensions, whether or not to return data that does not have values for all dimensions.
   * This will result in data rows that have varied Group Id lengths. The final limit after merging groups will not
   * be limited with this setting
   */
  allowAllPartialResults: boolean;
  /** If true, disables fallback results relying solely on primary results. */
  noFallback: boolean;
  /**
   * If true, computes total num groups for response.
   * Currently only supported for a single dimension in metrics.
   * Note: Funnels always returns num_groups and approximates when multiple dimensions are supplied.
   */
  computeNumGroups: boolean;
}

export enum SortOrder {
  SORT_ORDER_INVALID = 0,
  SORT_ORDER_ASC = 1,
  SORT_ORDER_DESC = 2
}

export enum SortType {
  /** Will default to aggregation */
  SORT_TYPE_INVALID = 0,
  /** Sort on whatever aggregation is selected. This is the default */
  SORT_TYPE_AGGREGATION = 1,
  /**
   * Force sorting to be applied based on default event count rather than selected aggregation (if different).
   * Note: This is currently not implemented for all metrics. Double check support before using.
   */
  SORT_TYPE_COUNT = 2,
  /**
   * Relative change is calculated by abs(past - current) / past
   * Is only supported for compare to past
   */
  SORT_TYPE_RELATIVE_CHANGE = 3,
  /**
   * Absolute change is calculated by abs(past - current)
   * Is only supported for compare to past
   */
  SORT_TYPE_ABSOLUTE_CHANGE = 4
}

export interface Metric {
  single?: Measure | null;
  /** Two measures combined using an arithmetic operator. */
  dual?: DualMeasure | null;
}

export namespace Metric {
  /** Target is measure or combination of measures. */
  export enum target {
    single = 1,
    /** Two measures combined using an arithmetic operator. */
    dual = 2
  }

  /** Target is measure or combination of measures. */
  /** Deprecated: use numeric constants instead of strings. */
  export enum target_keys {
    single = 'single',
    /** Two measures combined using an arithmetic operator. */
    dual = 'dual'
  }

  /** Target is measure or combination of measures. */
  export function target_oneof(m: Metric): { opt: target.dual, key: target_keys.dual, val: DualMeasure } | { opt: target.single, key: target_keys.single, val: Measure } | null {
    if (m.single != null) { return { opt: target.single, key: target_keys.single, val: m.single } }
    if (m.dual != null) { return { opt: target.dual, key: target_keys.dual, val: m.dual } }
    return null;
  }
}

export interface Measure {
  /** The primary event and dependent filters, if any. */
  event: segment.Event | null;
  /** Aggregation defines the property used for aggregation, along with the aggregation method. */
  aggregation: Aggregation | null;
  /**
   * The size of the window, in rolling_window_interval units.  Omitting this field
   * (0-value) is the same as 1.  Values > 1 will generate a rolling window
   * for which the property will be aggregated across. Windows for each day are
   * counted backwards, so for example a 7 day window starting on October 10th,
   * will aggregate data from [October 4th, October 11th)
   * 
   * This doesn't work for "ENTIRE_RANGE" intervals (obviously).
   */
  rollingWindow: number;
  /** Rolling window type specifies what kind of rolling window computation is being performed. */
  rollingWindowType: Measure.RollingWindowType;
  /** If computing a windowed metric, then this the size of each window */
  rollingWindowInterval: segment.TimeInterval;
}

export namespace Measure {
  export enum RollingWindowType {
    ROLLING_WINDOW_TYPE_INVALID = 0,
    /**
     * Rolling aggregation produces data points for each interval bucket which is the result of applying the selected
     * aggregation over the rolling_window specified.
     */
    ROLLING_WINDOW_TYPE_ROLLING_AGGREGATION = 1,
    /**
     * Rolling average produces data points for each interval bucket which is the result of applying an unweighted
     * average over the interval buckets applicable for the rolling_window specified.
     */
    ROLLING_WINDOW_TYPE_ROLLING_AVERAGE = 2
  }
}

export interface Aggregation {
  /** No property, defaults to event count. */
  default?: {} | null;
  /** Numeric properties w/ numeric aggregation methods. */
  numeric?: NumericAggregation | null;
  /** Properties present on each event. Only aggregation supported now is count unique. */
  unique?: UniqueAggregation;
  /** Custom properties. Only aggregated unique count is supported. */
  uniqueProperty?: UniquePropertyAggregation | null;
}

export namespace Aggregation {
  export enum aggregation_method {
    /** No property, defaults to event count. */
    default = 1,
    /** Numeric properties w/ numeric aggregation methods. */
    numeric = 2,
    /** Properties present on each event. Only aggregation supported now is count unique. */
    unique = 3,
    /** Custom properties. Only aggregated unique count is supported. */
    uniqueProperty = 4
  }

  /** Deprecated: use numeric constants instead of strings. */
  export enum aggregation_method_keys {
    /** No property, defaults to event count. */
    default = 'default',
    /** Numeric properties w/ numeric aggregation methods. */
    numeric = 'numeric',
    /** Properties present on each event. Only aggregation supported now is count unique. */
    unique = 'unique',
    /** Custom properties. Only aggregated unique count is supported. */
    uniqueProperty = 'uniqueProperty'
  }

  export function aggregation_method_oneof(m: Aggregation): { opt: aggregation_method.default, key: aggregation_method_keys.default, val: {} } | { opt: aggregation_method.numeric, key: aggregation_method_keys.numeric, val: NumericAggregation } | { opt: aggregation_method.unique, key: aggregation_method_keys.unique, val: UniqueAggregation } | { opt: aggregation_method.uniqueProperty, key: aggregation_method_keys.uniqueProperty, val: UniquePropertyAggregation } | null {
    if (m.default != null) { return { opt: aggregation_method.default, key: aggregation_method_keys.default, val: m.default } }
    if (m.numeric != null) { return { opt: aggregation_method.numeric, key: aggregation_method_keys.numeric, val: m.numeric } }
    if (m.unique != null) { return { opt: aggregation_method.unique, key: aggregation_method_keys.unique, val: m.unique } }
    if (m.uniqueProperty != null) { return { opt: aggregation_method.uniqueProperty, key: aggregation_method_keys.uniqueProperty, val: m.uniqueProperty } }
    return null;
  }
}

export interface DualMeasure {
  /** order matters when operators are not commutative. */
  first: Measure | null;
  second: Measure | null;
  operator: MeasureOperator;
  /** If set, configures the way the dimension groupings are applied for the metric (if dimensionality is configured). */
  dimensionApplication: DualMeasureDimensionApplication;
}

export enum MeasureOperator {
  MEASURE_OPERATOR_INVALID = 0,
  MEASURE_OPERATOR_ADD = 1,
  MEASURE_OPERATOR_SUBTRACT = 2,
  MEASURE_OPERATOR_MULTIPLY = 3,
  MEASURE_OPERATOR_DIVIDE = 4
}

export enum DualMeasureDimensionApplication {
  /** Will default to all */
  DIMENSION_APPLICATION_INVALID = 0,
  /** Apply the dimension grouping to all measures */
  DIMENSION_APPLICATION_ALL = 1,
  /** Apply the dimension grouping to the first measure only */
  DIMENSION_APPLICATION_FIRST = 2
}

export interface NumericAggregation {
  /** Scalar properties that can be used for numeric aggregations. */
  property: NumericProperty | null;
  /** Supported numeric aggregation methods. */
  aggregation: NumericAggregationMethod;
  percentileConfig?: NumericAggregationPercentileConfig | null;
}

export namespace NumericAggregation {
  /** Some aggregations have additional config. Should be set based on aggregation option. */
  export enum aggregation_config {
    percentileConfig = 3
  }

  /** Some aggregations have additional config. Should be set based on aggregation option. */
  export function aggregation_config_oneof(m: NumericAggregation): { opt: aggregation_config.percentileConfig, val: NumericAggregationPercentileConfig } | null {
    if (m.percentileConfig != null) { return { opt: aggregation_config.percentileConfig, val: m.percentileConfig } }
    return null;
  }
}

export interface NumericAggregationPercentileConfig {
  /** Percentile value should be within the range 0-100 inclusive */
  value: number;
}

export interface NumericProperty {
  builtInProperty?: BuiltInNumericProperty;
  /**
   * value should be of the form [property-name]_[data-type]
   * should not include 'evt_' prefix
   * supported types are int and float
   */
  customEventProperty?: string;
  /**
   * value should be of the form [property-name]_[data-type]
   * should not include 'user_' prefix
   */
  userVarProperty?: string;
  /**
   * value should be of the form [property-name]_[data-type]
   * should not include 'page_' prefix
   */
  pageProperty?: string;
  /**
   * value should be of the form [property-name]_[data-type]
   * should not include 'elem_' prefix
   */
  elementProperty?: string;
  /**
   * value should be a spec property slug
   * underlying property type should be numeric
   */
  specPropertySlug?: string;
}

export namespace NumericProperty {
  export enum field {
    builtInProperty = 1,
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'evt_' prefix
     * supported types are int and float
     */
    customEventProperty = 2,
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'user_' prefix
     */
    userVarProperty = 3,
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'page_' prefix
     */
    pageProperty = 4,
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'elem_' prefix
     */
    elementProperty = 5,
    /**
     * value should be a spec property slug
     * underlying property type should be numeric
     */
    specPropertySlug = 6
  }

  /** Deprecated: use numeric constants instead of strings. */
  export enum field_keys {
    builtInProperty = 'builtInProperty',
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'evt_' prefix
     * supported types are int and float
     */
    customEventProperty = 'customEventProperty',
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'user_' prefix
     */
    userVarProperty = 'userVarProperty',
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'page_' prefix
     */
    pageProperty = 'pageProperty',
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'elem_' prefix
     */
    elementProperty = 'elementProperty',
    /**
     * value should be a spec property slug
     * underlying property type should be numeric
     */
    specPropertySlug = 'specPropertySlug'
  }

  export function field_oneof(m: NumericProperty): { opt: field.builtInProperty, key: field_keys.builtInProperty, val: BuiltInNumericProperty } | { opt: field.customEventProperty, key: field_keys.customEventProperty, val: string } | { opt: field.elementProperty, key: field_keys.elementProperty, val: string } | { opt: field.pageProperty, key: field_keys.pageProperty, val: string } | { opt: field.specPropertySlug, key: field_keys.specPropertySlug, val: string } | { opt: field.userVarProperty, key: field_keys.userVarProperty, val: string } | null {
    if (m.builtInProperty != null) { return { opt: field.builtInProperty, key: field_keys.builtInProperty, val: m.builtInProperty } }
    if (m.customEventProperty != null) { return { opt: field.customEventProperty, key: field_keys.customEventProperty, val: m.customEventProperty } }
    if (m.userVarProperty != null) { return { opt: field.userVarProperty, key: field_keys.userVarProperty, val: m.userVarProperty } }
    if (m.pageProperty != null) { return { opt: field.pageProperty, key: field_keys.pageProperty, val: m.pageProperty } }
    if (m.elementProperty != null) { return { opt: field.elementProperty, key: field_keys.elementProperty, val: m.elementProperty } }
    if (m.specPropertySlug != null) { return { opt: field.specPropertySlug, key: field_keys.specPropertySlug, val: m.specPropertySlug } }
    return null;
  }
}

export enum BuiltInNumericProperty {
  NUMERIC_PROPERTY_INVALID = 0,
  NUMERIC_PROPERTY_PAGE_DURATION = 1,
  NUMERIC_PROPERTY_PAGE_ACTIVE_DURATION = 2,
  NUMERIC_PROPERTY_TIME_SINCE_PAGE_LOAD = 3,
  NUMERIC_PROPERTY_TIME_SINCE_SESSION_START = 4,
  NUMERIC_PROPERTY_CONSOLE_ERRORS_ON_PAGE = 5,
  NUMERIC_PROPERTY_CONSOLE_WARNINGS_ON_PAGE = 6,
  NUMERIC_PROPERTY_DISPLAY_WIDTH = 7,
  NUMERIC_PROPERTY_DISPLAY_HEIGHT = 8,
  NUMERIC_PROPERTY_VIEWPORT_WIDTH = 9,
  NUMERIC_PROPERTY_VIEWPORT_HEIGHT = 10,
  NUMERIC_PROPERTY_LOAD_EVENT_DURATION = 11,
  NUMERIC_PROPERTY_DOM_CONTENT_DURATION = 12,
  NUMERIC_PROPERTY_FIRST_MEANINGFUL_PAINT_DURATION = 13,
  NUMERIC_PROPERTY_SCROLL_DEPTH = 14,
  NUMERIC_PROPERTY_TIME_DURATION_RENDERED = 15,
  NUMERIC_PROPERTY_TIME_DURATION_VISIBLE = 16,
  NUMERIC_PROPERTY_PAGEVIEW_TOTAL_DURATION = 17,
  NUMERIC_PROPERTY_PAGEVIEW_ACTIVE_DURATION = 18,
  NUMERIC_PROPERTY_PAGEVIEW_INACTIVE_DURATION = 19,
  NUMERIC_PROPERTY_CUMULATIVE_LAYOUT_SHIFT = 20,
  NUMERIC_PROPERTY_FIRST_INPUT_DELAY = 21,
  NUMERIC_PROPERTY_LARGEST_CONTENTFUL_PAINT_DURATION = 22,
  NUMERIC_PROPERTY_INTERACTION_TO_NEXT_PAINT = 23,
  NUMERIC_PROPERTY_TIME_TO_INTERACTIVE = 24,
  NUMERIC_PROPERTY_TOTAL_BLOCKING_TIME = 25,
  NUMERIC_PROPERTY_TIME_TO_FIRST_BYTE = 26
}

export enum NumericAggregationMethod {
  NUMERIC_AGGREGATION_METHOD_INVALID = 0,
  /** sum of numeric properties values */
  NUMERIC_AGGREGATION_METHOD_SUM = 1,
  /** mean of numeric properties values */
  NUMERIC_AGGREGATION_METHOD_AVERAGE = 2,
  /** min of numeric properties values */
  NUMERIC_AGGREGATION_METHOD_MIN = 3,
  /** max of numeric properties values */
  NUMERIC_AGGREGATION_METHOD_MAX = 4,
  /** median of numeric property values */
  NUMERIC_AGGREGATION_METHOD_MEDIAN = 5,
  /** custom percentile of numeric property values */
  NUMERIC_AGGREGATION_METHOD_PERCENTILE = 6
}

export enum UniqueAggregation {
  UNIQUE_AGGREGATION_INVALID = 0,
  UNIQUE_AGGREGATION_INDIVIDUAL = 1,
  UNIQUE_AGGREGATION_SESSION = 2,
  UNIQUE_AGGREGATION_PAGEVIEW = 3
}

export interface UniquePropertyAggregation {
  /**
   * value should be of the form [property-name]_[data-type]
   * should not include 'evt_' prefix
   */
  customEventProperty?: string;
  /**
   * value should be of the form [property-name]_[data-type]
   * should not include 'user_' prefix
   */
  userVarProperty?: string;
  /**
   * value should be of the form [property-name]_[data-type]
   * should not include 'page_' prefix
   */
  pageProperty?: string;
  /**
   * value should be of the form [property-name]_[data-type]
   * should not include 'elem_' prefix
   */
  elementProperty?: string;
  /** value should be a spec property slug */
  specPropertySlug?: string;
}

export namespace UniquePropertyAggregation {
  export enum property {
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'evt_' prefix
     */
    customEventProperty = 1,
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'user_' prefix
     */
    userVarProperty = 2,
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'page_' prefix
     */
    pageProperty = 3,
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'elem_' prefix
     */
    elementProperty = 4,
    /** value should be a spec property slug */
    specPropertySlug = 5
  }

  /** Deprecated: use numeric constants instead of strings. */
  export enum property_keys {
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'evt_' prefix
     */
    customEventProperty = 'customEventProperty',
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'user_' prefix
     */
    userVarProperty = 'userVarProperty',
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'page_' prefix
     */
    pageProperty = 'pageProperty',
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'elem_' prefix
     */
    elementProperty = 'elementProperty',
    /** value should be a spec property slug */
    specPropertySlug = 'specPropertySlug'
  }

  export function property_oneof(m: UniquePropertyAggregation): { opt: property.customEventProperty, key: property_keys.customEventProperty, val: string } | { opt: property.elementProperty, key: property_keys.elementProperty, val: string } | { opt: property.pageProperty, key: property_keys.pageProperty, val: string } | { opt: property.specPropertySlug, key: property_keys.specPropertySlug, val: string } | { opt: property.userVarProperty, key: property_keys.userVarProperty, val: string } | null {
    if (m.customEventProperty != null) { return { opt: property.customEventProperty, key: property_keys.customEventProperty, val: m.customEventProperty } }
    if (m.userVarProperty != null) { return { opt: property.userVarProperty, key: property_keys.userVarProperty, val: m.userVarProperty } }
    if (m.pageProperty != null) { return { opt: property.pageProperty, key: property_keys.pageProperty, val: m.pageProperty } }
    if (m.elementProperty != null) { return { opt: property.elementProperty, key: property_keys.elementProperty, val: m.elementProperty } }
    if (m.specPropertySlug != null) { return { opt: property.specPropertySlug, key: property_keys.specPropertySlug, val: m.specPropertySlug } }
    return null;
  }
}

export interface Dimension {
  builtInProperty?: BuiltInDimension;
  /**
   * value should be of the form [property-name]_[data-type]
   * should not include 'evt_' prefix
   */
  customEventProperty?: string;
  /**
   * value should be of the form [property-name]_[data-type]
   * should not include 'user_' prefix
   */
  userVarProperty?: string;
  /**
   * value should be of the form [property-name]_[data-type]
   * should not include 'page_' prefix
   */
  pageProperty?: string;
  /**
   * value should be of the form [property-name]_[data-type]
   * should not include 'elem_' prefix
   */
  elementProperty?: string;
  /** value should contain a spec slug and some inferred data types */
  specProperty?: SpecPropertyDimension | null;
  /**
   * When non empty, limits dimension results to those that start with the provided prefix.
   * Additionally, the results will be rendered with the prefix stripped.
   * Useful example:
   *   built_in_property = DIMENSION_URL_QUERY
   *   prefix = "utm_source="
   *   -- will provide back top utm_source values from query params
   */
  prefix: string;
  /** When non zero, opts into per dimension limits. The top level limit is no longer applicable to the merged results. */
  limit: number;
  /**
   * When true, computes the metric at the dimension level (in addition to later levels).
   * Normally, results are only computed for all merged dimensions.
   * The results will include values whose dimensions are present up to this dimension with the corresponding aggregate value.
   */
  computeAtLevel: boolean;
  /**
   * If set, refines the dimensionality results at this level and below to the provided event.
   * Refinements stack as you work your way towards the leaf dimensions.
   */
  eventRefinement: segment.Event | null;
}

export namespace Dimension {
  export enum property {
    builtInProperty = 1,
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'evt_' prefix
     */
    customEventProperty = 2,
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'user_' prefix
     */
    userVarProperty = 3,
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'page_' prefix
     */
    pageProperty = 5,
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'elem_' prefix
     */
    elementProperty = 6,
    /** value should contain a spec slug and some inferred data types */
    specProperty = 10
  }

  /** Deprecated: use numeric constants instead of strings. */
  export enum property_keys {
    builtInProperty = 'builtInProperty',
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'evt_' prefix
     */
    customEventProperty = 'customEventProperty',
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'user_' prefix
     */
    userVarProperty = 'userVarProperty',
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'page_' prefix
     */
    pageProperty = 'pageProperty',
    /**
     * value should be of the form [property-name]_[data-type]
     * should not include 'elem_' prefix
     */
    elementProperty = 'elementProperty',
    /** value should contain a spec slug and some inferred data types */
    specProperty = 'specProperty'
  }

  export function property_oneof(m: Dimension): { opt: property.builtInProperty, key: property_keys.builtInProperty, val: BuiltInDimension } | { opt: property.customEventProperty, key: property_keys.customEventProperty, val: string } | { opt: property.elementProperty, key: property_keys.elementProperty, val: string } | { opt: property.pageProperty, key: property_keys.pageProperty, val: string } | { opt: property.specProperty, key: property_keys.specProperty, val: SpecPropertyDimension } | { opt: property.userVarProperty, key: property_keys.userVarProperty, val: string } | null {
    if (m.builtInProperty != null) { return { opt: property.builtInProperty, key: property_keys.builtInProperty, val: m.builtInProperty } }
    if (m.customEventProperty != null) { return { opt: property.customEventProperty, key: property_keys.customEventProperty, val: m.customEventProperty } }
    if (m.userVarProperty != null) { return { opt: property.userVarProperty, key: property_keys.userVarProperty, val: m.userVarProperty } }
    if (m.pageProperty != null) { return { opt: property.pageProperty, key: property_keys.pageProperty, val: m.pageProperty } }
    if (m.elementProperty != null) { return { opt: property.elementProperty, key: property_keys.elementProperty, val: m.elementProperty } }
    if (m.specProperty != null) { return { opt: property.specProperty, key: property_keys.specProperty, val: m.specProperty } }
    return null;
  }
}

export enum BuiltInDimension {
  DIMENSION_INVALID = 0,
  DIMENSION_CITY = 1,
  DIMENSION_STATE = 2,
  DIMENSION_COUNTRY = 3,
  DIMENSION_IP_ADDRESS = 37,
  DIMENSION_EMAIL = 4,
  DIMENSION_USER_ID = 53,
  DIMENSION_NAME = 5,
  DIMENSION_USER_AGENT = 6,
  DIMENSION_APP_VIEW_NAME = 7,
  DIMENSION_APP_PACKAGE_NAME = 38,
  DIMENSION_APP_NAME = 52,
  DIMENSION_DEVICE_MODEL = 8,
  DIMENSION_DEVICE_VENDOR = 9,
  DIMENSION_BROWSER = 10,
  DIMENSION_DEVICE = 11,
  DIMENSION_PLATFORM = 12,
  DIMENSION_OS = 13,
  DIMENSION_APP_VERSION = 14,
  DIMENSION_APP_OS_VERSION = 15,
  DIMENSION_APP_FS_VERSION = 51,
  DIMENSION_URL = 16,
  DIMENSION_URL_HOST = 17,
  DIMENSION_URL_FRAGMENT = 20,
  /**
   * For query param dimensionality, you more than likely want to supply a dimension prefix.
   * Query params have tokens indexed in two forms: <key>:<value> AND <value>.
   * Without a prefix (e.g. "utm_source="), you will get dimensionality counts for standalone values as well which could be misleading.
   */
  DIMENSION_URL_QUERY = 28,
  DIMENSION_URL_HASH_QUERY = 55,
  DIMENSION_URL_PATH = 44,
  DIMENSION_URL_HASH_PATH = 56,
  DIMENSION_REFERRER_URL = 21,
  DIMENSION_REFERRER_HOST = 22,
  DIMENSION_REFERRER_FRAGMENT = 25,
  /**
   * For query param dimensionality, you more than likely want to supply a dimension prefix.
   * Query params have tokens indexed in two forms: <key>:<value> AND <value>.
   * Without a prefix (e.g. "utm_source="), you will get dimensionality counts for standalone values as well which could be misleading.
   */
  DIMENSION_REFERRER_QUERY = 29,
  DIMENSION_REFERRER_PATH = 45,
  DIMENSION_TEXT = 26,
  DIMENSION_CSS = 27,
  DIMENSION_AJAX_REQUEST_METHOD = 30,
  DIMENSION_AJAX_REQUEST_STATUS = 31,
  DIMENSION_WEB_SOURCE_FILE_URL = 32,
  DIMENSION_WEB_SOURCE_FILE_URL_HOST = 33,
  DIMENSION_WEB_SOURCE_FILE_URL_FRAGMENT = 34,
  /**
   * For query param dimensionality, you more than likely want to supply a dimension prefix.
   * Query params have tokens indexed in two forms: <key>:<value> AND <value>.
   * Without a prefix (e.g. "utm_source="), you will get dimensionality counts for standalone values as well which could be misleading.
   */
  DIMENSION_WEB_SOURCE_FILE_URL_QUERY = 35,
  DIMENSION_WEB_SOURCE_FILE_URL_PATH = 46,
  DIMENSION_MOBILE_SOURCE_FILE = 36,
  DIMENSION_ERROR_CLICK_TYPE = 39,
  DIMENSION_PAGE_DEF_ID = 41,
  DIMENSION_BROWSER_VERSION = 42,
  DIMENSION_AJAX_REQUEST_URL = 57,
  DIMENSION_AJAX_REQUEST_URL_HOST = 58,
  DIMENSION_AJAX_REQUEST_URL_FRAGMENT = 59,
  DIMENSION_AJAX_REQUEST_URL_QUERY = 60,
  DIMENSION_AJAX_REQUEST_URL_PATH = 43,
  DIMENSION_SCREEN_WIDTH = 49,
  DIMENSION_SCREEN_HEIGHT = 50,
  DIMENSION_API_EVENT_NAME = 54,
  /** Only used by dataloss, hidden by the client */
  DIMENSION_DATALOSS_TYPE = 47,
  /** Only used by dataloss, hidden by the client */
  DIMENSION_DATALOSS_ORIGIN = 48
}

export interface SpecPropertyDimension {
  specSlug: string;
  /** scope to infer when taking dimension and producing filters */
  asScope: vars.Scope;
  /** type to infer when taking dimension and producing filters */
  asType: vars.Type;
}

export interface DataTable {
  table: DataRow[];
  /**
   * Total number of groups (including groups outside the top-N limit)
   * Only set if request computation.dimension_settings is set and num_groups is true
   */
  numGroups: protobuf.int64;
}

export interface DataRowGroup {
  groupId: string;
  /**
   * Fallback should be used in situations where the DataTable might be composed of
   * dimensions with varying identities. For example, if the top level request asks
   * for Elements and CSS selectors, DataRows containing Elements should be considered
   * primary, while those containing CSS selectors should be considered as fallbacks.
   * This information can then be used to render each DataRow appropriately.
   */
  fallback: boolean;
}

export interface DataRow {
  /**
   * Data Row is a record with 4 columns. First two columns
   * define the time bucket start and end for the aggregation window
   */
  startTime: protobuf.timestamp | null;
  endTime: protobuf.timestamp | null;
  /**
   * Determines the group(s) the row belongs to.
   * The num groups corresponds to the dimensionality. Can be empty.
   * If allowAllPartialResults setting is true, a DataTable may have DataRows with varied group lengths
   */
  groups: DataRowGroup[];
  /** Computed Aggregation value. */
  value: number;
  /**
   * Default value (typically event count) when available.
   * Note: This is currently not implemented for all metrics results. Double check support before using.
   */
  defaultValue: number;
  /**
   * True if the data row contains partial data for the time range
   * either because of retention cutoff or filtering
   * TODO(jb): Revisit if we want to handle retention cutoff separately
   */
  isPartialPeriod: boolean;
}
